// 8-speed gearbox (C9.6.1: eight forward gears) driving the gear display, RPM
// shift lights and engine sound.
//
// Two levels of fidelity:
// - Assisted (phone/tilt): shifting is automatic and cosmetic; the physics
//   uses a power-limited drive force and ignores the gearbox.
// - Engine model (wheel/sim mode): engine rpm comes from the gear and road
//   speed, or free-revs with the clutch open. ICE power follows an rpm curve,
//   the rev limiter cuts power, drive torque is limited by gearing at low
//   speed, and the clutch (pedal, or automatic launch/anti-stall) couples the
//   engine to the wheels. Shifting is automatic, sequential paddles, or a
//   6-speed H-pattern with the clutch.

export const GEAR_COUNT = 8;
const TOP_SPEED = 96; // m/s at the rev limiter in top gear (~345 km/h)
export const IDLE_RPM = 4000;
export const MAX_RPM = 12500;
const UPSHIFT_RPM = 12000;
const DOWNSHIFT_RPM = 8500;
/** Engine speed the launch clutch holds while slipping at full throttle (launch rpm). */
const LAUNCH_RPM = 11000;
/** Driven tyre rolling radius, m (18-inch wheels, C10.8). */
export const TYRE_RADIUS = 0.355;
// Top speed per gear as a fraction of TOP_SPEED (closer ratios in high gears).
const GEAR_TOPS = [0.3, 0.42, 0.53, 0.63, 0.72, 0.81, 0.9, 1.0];
// The G27 H-shifter has six forward gates: spread the same span over six gears.
const GEAR_TOPS_H = [0.32, 0.47, 0.6, 0.73, 0.86, 1.0];
/** Reverse is geared like a short first. */
const REVERSE_TOP = 0.2;

export type ShiftMode = 'auto' | 'sequential' | 'hpattern';

/**
 * ICE power share at an rpm, from the fuel energy-flow cap (C5.2.3-5):
 * EF = 0.27·rpm + 165 MJ/h below 10,500 rpm, 3000 MJ/h above. Power follows
 * the fuel energy available.
 */
export function icePowerCurve(rpm: number): number {
  return Math.max(0.05, Math.min(1, (0.27 * Math.max(0, rpm) + 165) / 3000));
}

export class Gearbox {
  /** −1 reverse, 0 neutral, 1..gearCount. */
  gear = 1;
  rpm = IDLE_RPM;
  /** Seconds since the last shift (for audio and HUD effects). */
  sinceShift = 1;
  mode: ShiftMode = 'auto';
  /** Engine model on (wheel/sim mode); off keeps the cosmetic gearbox. */
  engineModel = false;
  /** H-pattern: gears only engage with the clutch pressed. */
  requireClutch = true;
  /** Clutch coupling used for the last step, 0 (open) .. 1 (locked): how tied engine speed is to road speed. */
  coupling = 1;
  /**
   * Share of the engine's torque the clutch passes to the wheels, 0..1. A
   * slipping clutch still transmits its clamp torque: the ECU-controlled launch
   * clutch (paddles, automatic) passes all the engine torque while it slips; with
   * the pedal (H-pattern) it is the pedal's engagement.
   */
  torqueShare = 1;
  /** On the rev limiter (power cut). */
  limiter = false;
  /** Count of refused shifts (over-rev protection, no clutch); the HUD/audio can react. */
  refused = 0;
  /** Engine speed when not tied to the wheels (clutch open, neutral, launch). */
  private freeRpm = IDLE_RPM;

  get gearCount(): number {
    return this.mode === 'hpattern' ? GEAR_TOPS_H.length : GEAR_COUNT;
  }

  /** Road speed (m/s) at `rpm` in gear `g`. */
  speedAt(g: number, rpm: number): number {
    if (g === 0) return Infinity;
    const top = g < 0 ? REVERSE_TOP : (this.mode === 'hpattern' ? GEAR_TOPS_H : GEAR_TOPS)[Math.min(g, this.gearCount) - 1]!;
    return (TOP_SPEED * top * rpm) / MAX_RPM;
  }

  /** Overall ratio (crank turns per wheel turn) of gear `g`. */
  overallRatio(g: number): number {
    if (g === 0) return 0;
    return ((MAX_RPM * 2 * Math.PI) / 60) * TYRE_RADIUS / this.speedAt(g, MAX_RPM);
  }

  /** Engine friction braking force at the rear tyres for a crank torque, N (engine model). */
  engineBrakeForce(crankNm: number): number {
    if (!this.engineModel || this.gear === 0) return 0;
    const rpmShare = Math.min(1, this.rpm / MAX_RPM);
    return (crankNm * (0.4 + 0.6 * rpmShare) * this.overallRatio(this.gear) * this.coupling) / TYRE_RADIUS;
  }

  /** Engine rpm if the clutch were locked in gear `g` at `speed`. */
  wheelRpm(g: number, speed: number): number {
    return g === 0 ? 0 : (Math.abs(speed) / this.speedAt(g, MAX_RPM)) * MAX_RPM;
  }

  /** Switch shifting mode, keeping a sensible gear for the current speed. */
  setMode(mode: ShiftMode, speed = 0): void {
    if (mode === this.mode) return;
    this.mode = mode;
    this.gear = Math.max(1, Math.min(this.gearCount, this.bestGear(speed)));
  }

  /** Manual modes: jump to the gear that suits `speed` (after a scripted drive, e.g. the pit lane). */
  matchSpeed(speed: number): void {
    if (this.mode !== 'auto') this.gear = this.bestGear(speed);
  }

  private bestGear(speed: number): number {
    let g = 1;
    while (g < this.gearCount && this.wheelRpm(g, speed) > UPSHIFT_RPM * 0.92) g++;
    return g;
  }

  /** Sequential upshift (paddle). From reverse goes to neutral, from neutral to first. */
  shiftUp(): boolean {
    if (this.mode === 'auto' || this.gear >= this.gearCount) return false;
    this.gear = this.gear + 1;
    this.sinceShift = 0;
    return true;
  }

  /**
   * Sequential downshift (paddle). Refused if it would over-rev the engine, as
   * F1 gearbox control does. Below first: neutral, then reverse, only when
   * nearly stopped.
   */
  shiftDown(speed: number): boolean {
    if (this.mode === 'auto') return false;
    if (this.gear > 1) {
      if (this.wheelRpm(this.gear - 1, speed) > MAX_RPM * 1.02) {
        this.refused++;
        return false;
      }
    } else if (this.gear === 1 && Math.abs(speed) > 3) {
      return false;
    } else if (this.gear <= 0 && Math.abs(speed) > 1) {
      return false;
    }
    if (this.gear <= -1) return false;
    this.gear--;
    this.sinceShift = 0;
    return true;
  }

  /** H-pattern: the shifter's current gate (0 = neutral). `clutch` is the pedal, 0..1 pressed. */
  select(gate: number, clutch: number, speed = 0): void {
    if (gate === this.gear) return;
    if (gate < 0 && Math.abs(speed) > 1) {
      // Reverse won't go in while rolling: stay in neutral.
      if (this.gear !== 0) this.sinceShift = 0;
      this.gear = 0;
      return;
    }
    if (gate !== 0 && this.requireClutch && clutch < 0.6) {
      // No clutch: the gear won't go in. Count it once per attempt and sit in neutral.
      if (this.gear !== 0) this.refused++;
      if (this.gear !== 0) this.sinceShift = 0;
      this.gear = 0;
      return;
    }
    this.gear = Math.max(-1, Math.min(this.gearCount, gate));
    this.sinceShift = 0;
  }

  /**
   * Clutch coupling 0..1 for the next step. With a clutch pedal (H-pattern) it
   * follows the pedal (bite between 25% and 75% travel); otherwise it's an
   * automatic launch clutch. Either way, below idle the clutch slips so the
   * engine never stalls (F1 anti-stall).
   */
  updateCoupling(speed: number, clutchPedal: number): number {
    if (!this.engineModel || this.mode === 'auto') {
      this.coupling = this.torqueShare = this.gear === 0 ? 0 : 1;
      if (this.engineModel && this.gear !== 0) this.coupling = this.antiStall(speed, 1);
      return this.coupling;
    }
    // Neutral, or reverse while still rolling forwards: nothing drives the wheels.
    if (this.gear === 0 || (this.gear < 0 && speed > 1)) return (this.coupling = this.torqueShare = 0);
    const pedal = this.mode === 'hpattern' ? 1 - smoothstep(0.25, 0.75, clutchPedal) : 1;
    this.torqueShare = pedal;
    return (this.coupling = this.antiStall(speed, pedal));
  }

  private antiStall(speed: number, pedal: number): number {
    const w = this.wheelRpm(this.gear, speed);
    // Launch: as road speed brings the wheel side up towards idle, the clutch closes.
    const launch = Math.min(1, Math.max(0.12, w / IDLE_RPM));
    return pedal * launch;
  }

  /**
   * Drive power multiplier for the engine model: ICE share of `icePower` follows
   * the rpm curve, the limiter cuts everything. Returns the ICE factor; the
   * caller applies `coupling` and the gearing torque limit (`driveSpeed`).
   */
  iceFactor(): number {
    return this.limiter ? 0 : icePowerCurve(this.rpm);
  }

  /**
   * Speed that converts power to drive force (F = P / v). When the clutch is
   * slipping the engine turns faster than the wheels, so force is limited by
   * the gearing at the engine's rpm rather than growing without bound at low
   * road speed.
   */
  driveSpeed(speed: number): number {
    if (!this.engineModel) return Math.max(speed, 3);
    if (this.gear === 0) return Infinity;
    return Math.max(Math.abs(speed), this.speedAt(this.gear, this.rpm), 1);
  }

  update(speed: number, dt: number, throttle = 0): void {
    this.sinceShift += dt;
    if (!this.engineModel || this.mode === 'auto') this.autoShift(speed);
    if (!this.engineModel) {
      this.rpm = Math.max(IDLE_RPM, Math.min(MAX_RPM, this.wheelRpm(this.gear, speed)));
      this.limiter = false;
      this.coupling = 1;
      return;
    }
    // Free-revving target with the clutch open: blip with the throttle. In gear with
    // the clutch slipping (a launch), the clutch load holds the engine at launch rpm.
    const top = this.gear !== 0 && this.coupling > 0 && this.coupling < 1 ? LAUNCH_RPM : MAX_RPM;
    const freeTarget = IDLE_RPM + Math.min(1, Math.max(0, throttle)) * (top - IDLE_RPM);
    this.freeRpm += Math.min(1, dt / 0.12) * (freeTarget - this.freeRpm);
    const tied = this.wheelRpm(this.gear, speed);
    const c = this.coupling;
    // Slipping clutch: engine sits between its free speed and the wheels' speed.
    const rpm = this.gear === 0 ? this.freeRpm : c * tied + (1 - c) * Math.max(this.freeRpm, tied);
    this.rpm = Math.max(IDLE_RPM * 0.9, Math.min(MAX_RPM * 1.06, rpm));
    this.limiter = this.rpm >= MAX_RPM;
  }

  private autoShift(speed: number): void {
    if (this.gear < 1) this.gear = 1;
    const rpmIn = (g: number): number => this.wheelRpm(g, speed);
    if (this.gear < this.gearCount && rpmIn(this.gear) > UPSHIFT_RPM) {
      this.gear++;
      this.sinceShift = 0;
    } else if (this.gear > 1 && rpmIn(this.gear - 1) < DOWNSHIFT_RPM * 1.15 && rpmIn(this.gear) < DOWNSHIFT_RPM) {
      this.gear--;
      this.sinceShift = 0;
    }
  }

  /** 0..1 for the shift-light strip. */
  get shiftLight(): number {
    return Math.max(0, Math.min(1, (this.rpm - 9500) / (UPSHIFT_RPM - 9500)));
  }

  /** Gear as shown on the HUD. */
  get label(): string {
    return this.gear === 0 ? 'N' : this.gear < 0 ? 'R' : String(this.gear);
  }
}

function smoothstep(e0: number, e1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}
